import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { getMySubmissions } from '../../api/approvalApi';
import { signalRService }   from '../../services/signalRService';
import { SIGNALR_EVENTS } from '../../constants/signalREvents';
import { getSectionStyle }  from '../../utils/contentTypeStyles';
import ApprovalStatusBadge  from './ApprovalStatusBadge';
import ActionTypeChip, { splitConvertContentName } from './ActionTypeChip';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).format(new Date(dateStr));
}

const HEAD_COLS = ['Section', 'Content Name', 'Type', 'Status', 'Submitted', 'Decided By', 'Reason'];

export default function MySubmissionsPanel() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await getMySubmissions();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, load);
    return () => signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, load);
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
        <CircularProgress size={36} sx={{ color: '#0a326d' }} />
        <Typography variant="body2" sx={{ color: '#64748b' }}>Loading submissions…</Typography>
      </Box>
    );
  }

  if (!items.length) {
    return (
      <Box
        sx={{
          py: 8, px: 3,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          bgcolor: 'white', borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
        }}
      >
        <Box
          sx={{
            width: 60, height: 60, borderRadius: '16px',
            background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <InboxOutlinedIcon sx={{ fontSize: 28, color: '#94a3b8' }} />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography fontWeight={700} sx={{ color: '#1e293b', mb: 0.5 }}>No submissions yet</Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            Content you submit for approval will appear here
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      <Table size="small" sx={{ minWidth: 750 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f8fafc' }}>
            {HEAD_COLS.map((h) => (
              <TableCell
                key={h}
                align="left"
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  color: '#475569',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  py: 1.5,
                  px: 2,
                  borderBottom: '1px solid #e2e8f0',
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const sStyle = getSectionStyle(item.sectionLabel);
            const { baseName, targetLabel } = splitConvertContentName(item.contentName, item.actionType);
            return (
              <TableRow
                key={item.approvalId}
                sx={{
                  bgcolor: 'white',
                  transition: 'background 0.15s ease',
                  '&:hover': { bgcolor: '#f8fafc' },
                  '&:last-child td': { borderBottom: 0 },
                }}
              >
                {/* Section */}
                <TableCell sx={{ py: 1.5, px: 2 }}>
                  <Box
                    sx={{
                      display: 'inline-flex', alignItems: 'center',
                      px: 1.25, py: 0.45,
                      borderRadius: '6px',
                      bgcolor: sStyle.bg,
                      border: `1px solid ${sStyle.border}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: sStyle.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
                      {item.sectionLabel}
                    </Typography>
                  </Box>
                </TableCell>

                {/* Content Name */}
                <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: '#1e293b', fontWeight: 500 }}>
                  {baseName || '—'}
                </TableCell>

                {/* Type */}
                <TableCell sx={{ py: 1.5, px: 2 }}>
                  <ActionTypeChip actionType={item.actionType} targetLabel={targetLabel} />
                </TableCell>

                {/* Status */}
                <TableCell sx={{ py: 1.5, px: 2 }}>
                  <ApprovalStatusBadge status={item.status} actionType={item.actionType} />
                </TableCell>

                {/* Submitted */}
                <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: '#475569', whiteSpace: 'nowrap' }}>
                  {formatDate(item.submittedAt)}
                </TableCell>

                {/* Decided By */}
                <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: '#475569' }}>
                  {item.decidedByName || '—'}
                </TableCell>

                {/* Reason */}
                <Tooltip title={item.rejectionReason || ''} arrow placement="top">
                  <TableCell
                    sx={{
                      py: 1.5,
                      px: 2,
                      fontSize: '0.8125rem',
                      color: item.rejectionReason ? '#dc2626' : '#94a3b8',
                      maxWidth: { xs: 120, sm: 200 },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: item.rejectionReason ? 'help' : 'default',
                    }}
                  >
                    {item.rejectionReason || '—'}
                  </TableCell>
                </Tooltip>

              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
